import { createContext, useContext, useEffect, useState } from "react";

const FontSizeContext = createContext(null);

// Steps map to root font-size percentages: 0→90%, 1→100%, 2→112%, 3→125%
const SIZES = [90, 100, 112, 125];
const DEFAULT_STEP = 1; // index 1 → 100%
const MIN_STEP = 0;
const MAX_STEP = SIZES.length - 1;
// Key versioned to v2 so stale v1 values (stored as 0=80%) are ignored.
const STORAGE_KEY = "qf:font-step-global-v2";

export function FontSizeProvider({ children }) {
  const [step, setStep] = useState(() => {
    // A browser that has never touched A-/A+ has no stored key at all —
    // localStorage.getItem returns null, and Number(null) is 0, which is
    // itself a *valid* step (90%). That silently defaulted every
    // brand-new visitor to the smallest text size instead of 100%, and
    // then the effect below immediately persisted "0" back to
    // localStorage, baking the wrong default in permanently until they
    // happened to press A+ at least once. Distinguish "never set" from
    // "explicitly set to 0" before falling back to DEFAULT_STEP.
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_STEP;
    const v = Number(raw);
    return Number.isFinite(v) && v >= MIN_STEP && v <= MAX_STEP ? v : DEFAULT_STEP;
  });

  // Persists the user's own A-/A+ choice. Deliberately does NOT write
  // document.documentElement.style.fontSize here anymore — that's now
  // FontSizeDomSync's job (rendered inside I18nProvider, further down the
  // tree), because it also needs to fold in the Urdu Nastaliq size boost
  // and this provider sits *above* I18nProvider, with no access to `lang`.
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(step));
  }, [step]);

  const increase = () => setStep((s) => Math.min(s + 1, MAX_STEP));
  const decrease = () => setStep((s) => Math.max(s - 1, MIN_STEP));

  return (
    <FontSizeContext.Provider value={{
      step, increase, decrease,
      atMin: step === MIN_STEP, atMax: step === MAX_STEP,
      sizePercent: SIZES[step],
    }}>
      {children}
    </FontSizeContext.Provider>
  );
}

export const useFontSize = () => useContext(FontSizeContext);
